import React from 'react';
import PropTypes from 'prop-types';
import { f7 } from 'framework7-react';
import II18 from '../translate/II18';

import PaperflyAPI from '../../server/api/PaperflyAPI';
import Spinner from '../helpers/Spinner';
import {
  ESIGNATURE_TYPE,
  EXTERNAL_POPUP_WINDOW_STATUS,
  prepareExternalPopUpWindow,
  SET_EXTERNAL_POPUP_WINDOW_OPEN,
  setExternalPopUpWindowStatus,
  clearCreateTemplateRequestTask
} from '../../redux/actions/rootAction';
import { xconnect } from '../../redux/xconnect';
import {
  finalisePdfDoc,
  receiveESignatureData,
  requestDocumentESignature,
  resetDocumentESignatureStatus,
  setSignmeIdentityId
} from '../../redux/actions/esignature';
import {
  subscribeWebSocket,
  unSubscribeWebSocket
} from '../../redux/actions/websocket';
import { startCronJob, stopCronJob } from '../../redux/actions/cronJobs';
import { gae, gaeApp } from '../helpers/Analytics';
import { openYesPopupWin } from './esignatures/utils';
import SignmeRegisterPopUp from './esignatures/signme/SignmeRegisterPopUp';
import {
  findElementsWithoutValue,
  formStartQesDialog
} from '../helpers/formUtils';
import AtrustRegisterPopUp from './esignatures/atrust/AtrustRegisterPopUp';
import withESignatureUserData from './esignatures/withESignatureUserData';
import { isCreateTemplateRequestCancelled } from '../../redux/createTemplateRequestTask';
import { yesProvider } from './esignatures/providers';
import { createSignatureStatusWatch } from './esignatures/signatureStatusWatch';

/**
 * Thin QES shell: form gate, document prep, provider modal mount, YES signing path.
 * A-Trust / SignMe own create-process, popup lifecycle and status watch in their modals.
 */
export default function withESignature(WrappedComponent) {
  // noinspection JSPotentiallyInvalidUsageOfThis
  class withESignatureWrapper extends React.Component {
    constructor(props) {
      super(props);

      this.state = {
        signDocumentRequest: null,
        showRegistrationPopup: false,
        awaitDocumentIsReadyToSign: false,
        // When provider modal owns success UI, skip toast + immediate close
        deferSignSuccessToModal: false
      };

      this._signatureWatch = null;
    }

    providerModalOpen = () =>
      this.state.showRegistrationPopup === ESIGNATURE_TYPE.ATRUST ||
      this.state.showRegistrationPopup === ESIGNATURE_TYPE.SIGNME;

    ensureSignatureWatch = () => {
      if (this._signatureWatch) return this._signatureWatch;
      this._signatureWatch = createSignatureStatusWatch({
        startCronJob: this.props.startCronJob,
        stopCronJob: this.props.stopCronJob,
        subscribeWebSocket: this.props.subscribeWebSocket,
        unSubscribeWebSocket: this.props.unSubscribeWebSocket,
        getExternalPopUpStatus: () => this.props.externalPopUpStatus,
        receiveESignatureData: this.props.receiveESignatureData,
        onProcessFailed: () => this.stopWaitingAndShowSignDocumentError(),
        usePreloaderDialog: true,
        onPreloaderCancel: () => this.onStopESignProcess()
      });
      return this._signatureWatch;
    };

    componentDidUpdate(prevProps, prevState) {
      if (
        this.props.documentIsQualifiedSigned &&
        !prevProps.documentIsQualifiedSigned
      ) {
        this.onReceiveDocumentSignature(true);
        return;
      }

      if (
        this.props.documentFailOnQualifiedSign &&
        !prevProps.documentFailOnQualifiedSign
      ) {
        this.onReceiveDocumentSignature(false);
        return;
      }

      if (
        this.state.awaitDocumentIsReadyToSign &&
        (this.isRequestJustPrepared(prevProps) ||
          this.isDocumentJustFinalised(prevProps) ||
          this.isErrorOnPrepareDocumentOccurred())
      ) {
        this.prepareDocumentToSign();
        return;
      }

      if (
        this.state.awaitDocumentIsReadyToSign &&
        isCreateTemplateRequestCancelled(
          this.props.createTemplateRequestTask
        ) &&
        !isCreateTemplateRequestCancelled(prevProps.createTemplateRequestTask)
      ) {
        gaeApp('CREATE_TEMPLATE_REQUEST_CANCELLED', 'esign');
        this.props.clearCreateTemplateRequestTask();
        this.onStopPrepareTemplate();
        return;
      }

      // YES path: create process once document prep finishes
      if (this.state.awaitCreateESignatureProcessReady) {
        if (this.isDocumentJustReadyToSign(prevState)) {
          this.createYesESignatureProcess();
        }
      }

      // YES: user closed provider popup while signing — pause poll
      if (
        this.getESignatureType() === ESIGNATURE_TYPE.YES &&
        this.state.signDocumentRequest &&
        !this.props.documentIsQualifiedSigned &&
        this.props.externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.CLOSE &&
        prevProps.externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN
      ) {
        gae('ESIGN_PROCESS', 'EXT_POPUP_USER_CLOSED_PAUSE_POLL');
        this.ensureSignatureWatch().pause();
      }

      if (
        this.props.signatureProcessItem?.isQesWidget &&
        !prevProps.signatureProcessItem &&
        !this.props.documentIsQualifiedSigned
      ) {
        gaeApp('ESIGN_PROCESS.QUALIFIED_SIGNATURE_WIDGET_ACTIVATED');
        this.prepareESignatureProcess();
      }
    }

    componentWillUnmount() {
      this._signatureWatch?.pause();
    }

    isErrorOnPrepareDocumentOccurred = () =>
      this.props.templateRequestPreparedError || this.props.finalisePdfDocError;

    isDocumentJustReadyToSign = prevState =>
      !this.state.awaitDocumentIsReadyToSign &&
      prevState.awaitDocumentIsReadyToSign;

    isRequestJustPrepared = prevProps =>
      !this.props.templateMode && prevProps.templateMode;

    isDocumentJustFinalised = prevProps =>
      this.props.document?.finalised && !prevProps.document?.finalised;

    setWaitingForProcessing = as => {
      this.setState({
        waitingForProcessing: as
      });
    };

    showSuccessfulySignedToast = () => {
      gae('ESIGN_PROCESS', 'SIGNATURE_SUCCESSFUL');

      const toast = this.$f7.toast.create({
        cssClass: 'success-toast',
        icon: '<i class="material-icons">check_circle_outline</i>',
        text: II18.get('esignature_document_signed'),
        position: 'center',
        closeTimeout: 3000
      });
      toast.open();
    };

    closeExternalPopUp = () => {
      gae('ESIGN_PROCESS', 'CLOSE_EXT_POPUP');

      this.props.callAction(
        SET_EXTERNAL_POPUP_WINDOW_OPEN,
        EXTERNAL_POPUP_WINDOW_STATUS.CLOSE
      );
    };

    onReceiveDocumentSignature = success => {
      gae('ESIGN_PROCESS', 'PROCESS_DONE', success);
      this.closeExternalPopUp();
      this._signatureWatch?.pause();
      this.setWaitingForProcessing(false);

      if (success) {
        if (this.providerModalOpen() || this.state.deferSignSuccessToModal) {
          // Modal shows success step + Fertig / auto-close 3s
          this.setState({ deferSignSuccessToModal: true });
          return;
        }
        this.showSuccessfulySignedToast();
        this.onStopESignProcess();
      } else {
        this.stopWaitingAndShowSignDocumentError();
      }
    };

    onSignFlowDoneFromModal = () => {
      this.setState({ deferSignSuccessToModal: false });
      this.onStopESignProcess();
      // Toast after modal closes (success was deferred while QesSuccessFooter was open)
      this.showSuccessfulySignedToast();
    };

    /**
     * YES only: redirect browser popup and start status watch.
     */
    doSubmitYesSignRequestToPopUp = () => {
      gae('ESIGN_PROCESS', 'SUBMIT_DATA_TO_EXT_POPUP');

      this.props.requestESignatureDocument(
        this.props.document.id,
        this.state.signDocumentRequest.internalCallbackId
      );

      yesProvider.submitToPopup(
        this.props.externalPopUpOptions.winReference,
        this.state.signDocumentRequest.url
      );

      this.setWaitingForProcessing(false);
      if (!this.props.documentIsQualifiedSigned) {
        this.ensureSignatureWatch().start(
          this.state.signDocumentRequest.internalCallbackId,
          yesProvider
        );
      }
    };

    finaliseDocument = () => {
      gaeApp('ESIGN_PROCESS.START_FINALISE_PDF_DOC');
      this.props.finalisePdfDoc(
        this.props.document.id,
        this.props.recipientRequestId,
        [this.props.recipientId],
        this.props.actualFormData
      );
    };

    stopWaitingAndShowSignDocumentError = err => {
      gae('ESIGN_PROCESS', 'ERROR', err);
      this.onStopESignProcess();
      this.props.closeExtPopUp();

      f7.dialog.alert(
        II18.get('esignature_sign_document_error_message', err),
        II18.get('esignature_sign_document_error_title')
      );
    };

    /**
     * YES signing: create process (or reuse) and submit to external popup.
     * A-Trust / SignMe create+submit live in their register modals.
     */
    createYesESignatureProcess = () => {
      gae('ESIGN_PROCESS', 'CREATE_ESIGN_PROCESS');
      this.props.resetDocumentESignatureStatus(this.props.document.id);

      if (this.state.signDocumentRequest) {
        this.props.setYesPopUpWindowOpen();
        setTimeout(this.doSubmitYesSignRequestToPopUp, 300);
        return;
      }

      if (!this.isDocumentReadyToSign()) {
        gae('ESIGN_PROCESS', 'AWAIT_START_PROCESS', 'DOCUMENT_IS_NOT_READY');
        this.setState(
          { awaitCreateESignatureProcessReady: true },
          this.prepareDocumentToSign
        );
        return;
      }
      this.setState({ awaitCreateESignatureProcessReady: false });

      PaperflyAPI.yesCreateSignatureProcess(this.props.recipientRequestId, [
        {
          documentId: this.props.document.id,
          widgetId: this.props.signatureProcessItem?.id
        }
      ]).then(
        response => {
          if (response.error) {
            this.stopWaitingAndShowSignDocumentError(response.error);
          } else {
            this.setState(
              {
                signDocumentRequest: {
                  url: response.url,
                  internalCallbackId: response.internalCallbackId
                }
              },
              this.doSubmitYesSignRequestToPopUp
            );
          }
        },
        error => {
          this.stopWaitingAndShowSignDocumentError(
            error.response.data.error || error.response.data.errorCode
          );
        }
      );
    };

    onStopESignProcess = () => {
      gae('ESIGN_PROCESS', 'STOP');

      this._signatureWatch?.pause();
      this.closeExternalPopUp();
      this.setState({
        showRegistrationPopup: false,
        waitingForProcessing: false,
        awaitCreateESignatureProcessReady: false,
        awaitDocumentIsReadyToSign: false,
        deferSignSuccessToModal: false,
        signDocumentRequest: null
      });
      this.props.onStopSignatureProcess();
    };

    /**
     * Starting ESignature process for one document.
     * Process start by click on Sign button.
     */
    startESignatureProcess = () => {
      gae('ESIGN_PROCESS', 'START_ESIGN_PROCESS');
      this.props.initWebsocket();

      gae('ESIGN_PROCESS', 'SELECT_SIGN_PROVIDER', this.getESignatureType());

      switch (this.getESignatureType()) {
        case ESIGNATURE_TYPE.SIGNME:
          gae('SIGNME_SIGN_PROCESS', 'START_SIGN');
          this.setState({
            showRegistrationPopup: ESIGNATURE_TYPE.SIGNME,
            waitingForProcessing: false,
            deferSignSuccessToModal: true
          });
          break;
        case ESIGNATURE_TYPE.YES:
          this.setWaitingForProcessing(true);
          this.props.setYesPopUpWindowOpen();
          this.createYesESignatureProcess();
          break;
        case ESIGNATURE_TYPE.ATRUST:
          gae('ATRUST_SIGN_PROCESS', 'START_REGISTRATION');
          this.setState({
            showRegistrationPopup: ESIGNATURE_TYPE.ATRUST,
            waitingForProcessing: false,
            deferSignSuccessToModal: true
          });
          break;
        default:
          this.setWaitingForProcessing(false);
          f7.dialog.alert(
            II18.get('esign_sign_type_not_supported', this.getESignatureType()),
            II18.get('common_error')
          );
          break;
      }
    };

    getESignatureType = () => this.props.qesProvider;

    prepareESignatureProcess = () => {
      gae('ESIGN_PROCESS', 'PREPARE_SIGN', 'START');

      if (this.props.formErrors?.size === 1) {
        if (
          findElementsWithoutValue(
            this.props.formStructure,
            this.props.formResult,
            [this.props.signatureProcessItem.id]
          )
        ) {
          formStartQesDialog(
            () => this.props.onStopSignatureProcess(),
            () => this.startESignatureProcess()
          );
        } else {
          this.startESignatureProcess();
        }
      } else {
        this.$f7.dialog.alert(II18.get('dialog_fill_all_required'));
        this.props.onStopSignatureProcess();
      }
    };

    onSignmeRegistrationCancelled = () => {
      gae('ESIGN_REGISTRATION', 'CANCELLED');
      this.props.closeExtPopUp();
      this.setState({ showRegistrationPopup: false });
      this.onStopESignProcess();
      gae('ESIGN_REGISTRATION', 'CANCEL_SIGNME_IDENTITYID');
      this.props.setSignmeIdentityId('');
    };

    onStopPrepareTemplate = () => {
      gae('ESIGN_PROCESS', 'CANCEL_PREPARE_TEMPLATE_REQUEST');

      if (this.state.showRegistrationPopup) {
        this.setState({
          awaitDocumentIsReadyToSign: false,
          waitingForProcessing: false
        });
      } else {
        this.onStopESignProcess();
      }
    };

    prepareTemplateRequest = () => {
      const modalOwnsUi = this.providerModalOpen();
      this.props.startCreateTemplateRequest({
        customRecipientsDataSource: [this.props.eSignatureRecipientData],
        customTitle: II18.get('title_next'),
        // Provider modal step list owns progress — skip global Preparing alert
        skipPreparingDialog: modalOwnsUi,
        // Embed credentials screen in provider modal (no second popup)
        embedInCaller: modalOwnsUi
      });
    };

    prepareDocumentToSign = () => {
      gae('ESIGN_PROCESS', 'PREPARE_REQUEST_AND_DOCUMENT_TO_SIGN');

      if (this.isErrorOnPrepareDocumentOccurred()) {
        gae('ESIGN_PROCESS', 'ERROR_ON_PREPARE_REQUEST_AND_DOCUMENT_TO_SIGN');
        this.stopWaitingAndShowSignDocumentError(
          this.props.templateRequestPreparedError ||
            this.props.finalisePdfDocError
        );
      } else if (this.isDocumentReadyToSign()) {
        gae(
          'ESIGN_PROCESS',
          'PREPARE_REQUEST_AND_DOCUMENT_TO_SIGN',
          'SUCCESSFUL'
        );
        this.setState({
          awaitDocumentIsReadyToSign: false,
          waitingForProcessing: false
        });
      } else {
        const modalOwnsWaitUi = this.providerModalOpen();
        this.setState(
          {
            awaitDocumentIsReadyToSign: true,
            waitingForProcessing: !modalOwnsWaitUi
          },
          () => {
            if (this.props.templateMode) {
              this.prepareTemplateRequest();
            } else if (!this.props.document?.finalised) {
              this.finaliseDocument();
            }
          }
        );
      }
    };

    isDocumentReadyToSign = () =>
      !this.props.templateMode && this.props.document?.finalised;

    render() {
      return (
        <>
          <WrappedComponent
            {...this.props}
            signatureProcessItem={
              this.props.signatureProcessItem?.isQesWidget
                ? null
                : this.props.signatureProcessItem
            }
          />

          {this.getESignatureType() === ESIGNATURE_TYPE.SIGNME && (
            <SignmeRegisterPopUp
              docId={this.props.document.id}
              signatureFieldId={this.props.signatureProcessItem?.id}
              opened={
                this.state.showRegistrationPopup === ESIGNATURE_TYPE.SIGNME
              }
              onClose={this.onStopESignProcess}
              onRegistrationCancelled={this.onSignmeRegistrationCancelled}
              documentIsReadyToSign={this.isDocumentReadyToSign()}
              prepareDocumentToSign={this.prepareDocumentToSign}
              prepareDocumentToSignError={this.isErrorOnPrepareDocumentOccurred()}
              currentRecipientData={this.props.currentRecipientData}
              waitingForProcessing={this.state.waitingForProcessing}
              setWaitingForProcessing={this.setWaitingForProcessing}
              templateMode={this.props.templateMode}
              signmeIdentityId={this.props.signmeIdentityId}
              documentIsQualifiedSigned={this.props.documentIsQualifiedSigned}
              onSignFlowDone={this.onSignFlowDoneFromModal}
            />
          )}

          {this.getESignatureType() === ESIGNATURE_TYPE.ATRUST && (
            <AtrustRegisterPopUp
              docId={this.props.document.id}
              currentRecipientData={this.props.currentRecipientData}
              signatureFieldId={this.props.signatureProcessItem?.id}
              opened={
                this.state.showRegistrationPopup === ESIGNATURE_TYPE.ATRUST
              }
              onClose={this.onStopESignProcess}
              documentIsReadyToSign={this.isDocumentReadyToSign()}
              prepareDocumentToSign={this.prepareDocumentToSign}
              prepareDocumentToSignError={this.isErrorOnPrepareDocumentOccurred()}
              templateMode={this.props.templateMode}
              documentIsQualifiedSigned={this.props.documentIsQualifiedSigned}
              onSignFlowDone={this.onSignFlowDoneFromModal}
            />
          )}

          {this.state.waitingForProcessing && !this.providerModalOpen() && (
            <Spinner id={'eSignaturesSpinner'} fullScreen />
          )}
        </>
      );
    }
  }

  withESignatureWrapper.propTypes = {
    document: PropTypes.object,
    initWebsocket: PropTypes.func,
    recipientRequestId: PropTypes.string,
    recipientId: PropTypes.string,
    signmeIdentityId: PropTypes.string,
    templateMode: PropTypes.bool,
    documentIsQualifiedSigned: PropTypes.bool,
    documentFailOnQualifiedSign: PropTypes.bool,
    finalisePdfDocError: PropTypes.bool,
    templateRequestPreparedError: PropTypes.bool,
    qualifiedSignatureInfo: PropTypes.object,
    externalPopUpStatus: PropTypes.any,
    setYesPopUpWindowOpen: PropTypes.func,
    setSignmeIdentityId: PropTypes.func,
    resetDocumentESignatureStatus: PropTypes.func,
    requestESignatureDocument: PropTypes.func,
    receiveESignatureData: PropTypes.func,
    qesProvider: PropTypes.string,
    startCronJob: PropTypes.func,
    stopCronJob: PropTypes.func,
    subscribeWebSocket: PropTypes.func,
    unSubscribeWebSocket: PropTypes.func,
    finalisePdfDoc: PropTypes.func,
    callAction: PropTypes.func,
    closeExtPopUp: PropTypes.func,
    onStartSignatureProcess: PropTypes.func,
    externalPopUpOptions: PropTypes.object,
    actualFormData: PropTypes.object,
    formErrors: PropTypes.object,
    formResult: PropTypes.object,
    readyToSignatureItem: PropTypes.any,
    signatureProcessItem: PropTypes.object,
    formStructure: PropTypes.array,
    onStopSignatureProcess: PropTypes.func,
    eSignatureRecipientData: PropTypes.object,
    currentRecipientData: PropTypes.object,
    usersData: PropTypes.array,
    createTemplateRequestTask: PropTypes.object,
    startCreateTemplateRequest: PropTypes.func,
    clearCreateTemplateRequestTask: PropTypes.func
  };

  const mapStateToProps = (state, ownProps) => {
    const requestedDocument = ownProps.document;
    const qualifiedSignature = state.signatures[requestedDocument?.id];

    return {
      finalisePdfDocError: state.finalisePdfDocError,
      templateRequestPreparedError: state.templateRequestPreparedError,
      createTemplateRequestTask: state.createTemplateRequestTask,
      preparedFormsData: ownProps.preparedFormsData,
      preparedFormsStructures: ownProps.preparedFormsStructures,
      actualFormData:
        ownProps.preparedFormsData.get(requestedDocument?.id) || new Map(),
      templateMode: state.templateMode,
      qesProvider: state.qesProvider,
      externalPopUpOptions: state.externalPopUpOptions,
      externalPopUpStatus: state.externalPopUpStatus,
      documentIsQualifiedSigned:
        qualifiedSignature?.success || requestedDocument?.qualifiedSigned,
      documentFailOnQualifiedSign: qualifiedSignature?.fail,
      qualifiedSignatureInfo: qualifiedSignature?.postSigningPages,
      recipientRequestId: state.recipientRequestId,
      recipientId: state.recipientId,
      signmeIdentityId: state.signmeIdentityId
    };
  };

  const mapDispatchToProps = dispatch => ({
    finalisePdfDoc: (
      docId,
      recipientRequestId,
      recipientIdArray,
      formDataForSign
    ) =>
      dispatch(
        finalisePdfDoc(
          docId,
          recipientRequestId,
          recipientIdArray,
          formDataForSign
        )
      ),
    clearCreateTemplateRequestTask: () =>
      dispatch(clearCreateTemplateRequestTask()),
    resetDocumentESignatureStatus: docId =>
      dispatch(resetDocumentESignatureStatus(docId)),
    requestESignatureDocument: (docId, internalCallbackId) =>
      dispatch(requestDocumentESignature(docId, internalCallbackId)),
    setSignmeIdentityId: identityId =>
      dispatch(setSignmeIdentityId(identityId)),
    startCronJob: (id, period, doJob, timeOut) =>
      dispatch(startCronJob(id, period, doJob, timeOut)),
    stopCronJob: id => dispatch(stopCronJob(id)),
    subscribeWebSocket: (topic, sid) =>
      dispatch(subscribeWebSocket(topic, sid)),
    unSubscribeWebSocket: topic => dispatch(unSubscribeWebSocket(topic)),
    receiveESignatureData: data => dispatch(receiveESignatureData(data)),
    setYesPopUpWindowOpen: () =>
      dispatch(prepareExternalPopUpWindow(openYesPopupWin())),
    closeExtPopUp: () =>
      dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE))
  });
  return xconnect(
    mapStateToProps,
    mapDispatchToProps
  )(withESignatureUserData(withESignatureWrapper));
}
